import * as A from 'astronomy-engine';
import { Vec3, norm, raDecToVec } from '../lib/math';

export type BodyId = 'Sun' | 'Moon' | 'Mercury' | 'Venus' | 'Mars' | 'Jupiter' | 'Saturn' | 'Uranus' | 'Neptune';

export const BODY_IDS: BodyId[] = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune'];

// Astronomy Engine misspells four constellations; these are the IAU's spellings.
const IAU_SPELLING: Record<string, string> = { Antila: 'Antlia', Bootes: 'Boötes', Camelopardis: 'Camelopardalis', 'Pisces Austrinus': 'Piscis Austrinus' };
/** The constellation at a J2000 position (right ascension and declination in degrees): its IAU abbreviation and name. */
export function constellationAt(ra: number, dec: number): { id: string; name: string } {
  const c = A.Constellation(ra / 15, dec);
  return { id: c.symbol, name: IAU_SPELLING[c.name] ?? c.name };
}
/** The IAU name of the constellation at a J2000 position. */
export const constellationName = (ra: number, dec: number) => constellationAt(ra, dec).name;

const RADIUS_KM: Record<BodyId, number> = {
  Sun: 695700, Moon: 1737.4, Mercury: 2439.7, Venus: 6051.8, Mars: 3389.5,
  Jupiter: 69911, Saturn: 58232, Uranus: 25362, Neptune: 24622,
};

/** Display tint (sRGB). */
export const BODY_COLOR: Record<BodyId, [number, number, number]> = {
  Sun: [1.0, 0.96, 0.88], Moon: [0.96, 0.94, 0.9], Mercury: [0.86, 0.8, 0.74], Venus: [1.0, 0.97, 0.9],
  Mars: [1.0, 0.64, 0.45], Jupiter: [0.98, 0.9, 0.8], Saturn: [0.98, 0.88, 0.68], Uranus: [0.72, 0.9, 0.95],
  Neptune: [0.58, 0.7, 1.0],
};

// IAU north pole directions (J2000), for disc orientation.
const POLE: Partial<Record<BodyId, Vec3>> = {
  Sun: raDecToVec(286.13, 63.87),
  Mercury: raDecToVec(281.01, 61.41),
  Venus: raDecToVec(272.76, 67.16),
  Uranus: raDecToVec(257.311, -15.175),
  Neptune: raDecToVec(299.36, 43.46),
  Moon: raDecToVec(269.9949, 66.5392),
  Saturn: raDecToVec(40.589, 83.537),
  Jupiter: raDecToVec(268.057, 64.495),
  Mars: raDecToVec(317.681, 52.887),
};

const AU_KM = 149597870.7;

export interface BodyState {
  id: BodyId;
  /** Topocentric J2000 unit vector (light-time and aberration corrected). */
  dir: Vec3;
  /** Topocentric J2000 position, AU. */
  pos: Vec3;
  distAu: number;
  mag: number;
  phaseFraction: number;
  phaseAngle: number;
  angDiamDeg: number;
  /** Unit vector from the body towards the Sun (J2000). */
  toSun: Vec3;
  pole?: Vec3;
  /** Libration (Moon only): sub-observer selenographic latitude/longitude, degrees. */
  libLat?: number;
  libLon?: number;
  ringTilt?: number;
}

export function computeBodies(date: Date, observer: A.Observer): BodyState[] {
  const sunEq = A.Equator(A.Body.Sun, date, observer, false, true);
  const sunPos: Vec3 = [sunEq.vec.x, sunEq.vec.y, sunEq.vec.z];
  const out: BodyState[] = [];
  for (const id of BODY_IDS) {
    const eq = id === 'Sun' ? sunEq : A.Equator(A.Body[id], date, observer, false, true);
    const pos: Vec3 = [eq.vec.x, eq.vec.y, eq.vec.z];
    const dist = eq.dist;
    const dir = norm(pos);
    let mag = -26.74, phaseFraction = 1, phaseAngle = 0, ringTilt: number | undefined;
    if (id !== 'Sun') {
      const ill = A.Illumination(A.Body[id], date);
      mag = ill.mag;
      phaseFraction = ill.phase_fraction;
      phaseAngle = ill.phase_angle;
      if (id === 'Saturn') ringTilt = ill.ring_tilt;
    }
    const toSun = id === 'Sun' ? dir : norm([sunPos[0] - pos[0], sunPos[1] - pos[1], sunPos[2] - pos[2]]);
    const angDiamDeg = (2 * Math.asin(Math.min(1, RADIUS_KM[id] / (dist * AU_KM))) * 180) / Math.PI;
    const b: BodyState = { id, dir, pos, distAu: dist, mag, phaseFraction, phaseAngle, angDiamDeg, toSun, pole: POLE[id], ringTilt };
    if (id === 'Moon') {
      const lib = A.Libration(date);
      b.libLat = lib.elat;
      b.libLon = lib.elon;
    } else if (id === 'Mars' || id === 'Mercury' || id === 'Venus') {
      // Which way the surface faces us, so its map sits right: the sub-observer point.
      const sub = subObserverPoint(A.Body[id], date, dir, dist);
      b.libLat = sub.lat;
      b.libLon = sub.lon;
    }
    out.push(b);
  }
  return out;
}

export function moonPhaseName(phaseDeg: number): string {
  const p = ((phaseDeg % 360) + 360) % 360;
  if (p < 6 || p >= 354) return 'New Moon';
  if (p < 84) return 'Waxing crescent';
  if (p < 96) return 'First quarter';
  if (p < 174) return 'Waxing gibbous';
  if (p < 186) return 'Full Moon';
  if (p < 264) return 'Waning gibbous';
  if (p < 276) return 'Last quarter';
  return 'Waning crescent';
}

export interface RiseSet {
  rise: Date | null;
  transit: Date | null;
  set: Date | null;
  transitAlt: number | null;
  circumpolar: boolean;
  neverRises: boolean;
}

/**
 * Rise, transit and set for the current pass: if the object is up now, when it rose and when it will set;
 * otherwise when it next rises and sets. `transit` is the highest point of that same pass.
 */
export function riseTransitSet(body: A.Body, observer: A.Observer, now: Date): RiseSet {
  const prevRise = A.SearchRiseSet(body, observer, +1, now, -1.5);
  const prevSet = A.SearchRiseSet(body, observer, -1, now, -1.5);
  const nextRise = A.SearchRiseSet(body, observer, +1, now, 1.5);
  const nextSet = A.SearchRiseSet(body, observer, -1, now, 1.5);
  const any = prevRise || prevSet || nextRise || nextSet;
  let up: boolean;
  if (any) up = !!prevRise && (!prevSet || prevRise.ut > prevSet.ut) || (!prevRise && !prevSet && !!nextSet && (!nextRise || nextSet.ut < nextRise.ut));
  else {
    const eq = A.Equator(body, now, observer, true, true);
    up = A.Horizon(now, observer, eq.ra, eq.dec, 'normal').altitude > 0;
  }
  const rise = up ? prevRise : nextRise;
  // Start a minute after the rise: near the poles a grazing rise and set can otherwise coincide.
  const set = up ? nextSet : nextRise ? A.SearchRiseSet(body, observer, -1, new Date(nextRise.date.getTime() + 60e3), 1.5) : null;
  let transit: Date | null = null, transitAlt: number | null = null;
  try {
    // The meridian crossing of this pass: after the rise if there is one, else the nearest to now.
    const from = rise ? rise.date : new Date(now.getTime() - 12 * 3600e3);
    const t = A.SearchHourAngle(body, observer, 0, from);
    transit = t.time.date;
    transitAlt = t.hor.altitude;
  } catch {
    /* ignore */
  }
  const circumpolar = !any && up;
  const neverRises = !any && !up;
  return { rise: rise?.date ?? null, transit, set: set?.date ?? null, transitAlt, circumpolar, neverRises };
}

let starSlot = 0;
/** Rise/transit/set for a fixed J2000 direction (star or deep-sky object). */
export function riseTransitSetFixed(raDeg: number, decDeg: number, observer: A.Observer, from: Date): RiseSet {
  const slots = [A.Body.Star1, A.Body.Star2, A.Body.Star3, A.Body.Star4];
  const body = slots[starSlot++ % slots.length];
  A.DefineStar(body, raDeg / 15, decDeg, 1000);
  return riseTransitSet(body, observer, from);
}

export interface TwilightTimes {
  sunset: Date | null;
  civilDusk: Date | null;
  nauticalDusk: Date | null;
  astroDusk: Date | null;
  astroDawn: Date | null;
  nauticalDawn: Date | null;
  civilDawn: Date | null;
  sunrise: Date | null;
}

/** Evening and following-morning twilight around the night that starts on the local evening of `day`. */
export function twilight(observer: A.Observer, noon: Date): TwilightTimes {
  const find = (dir: number, alt: number, start: Date) => A.SearchAltitude(A.Body.Sun, observer, dir, start, 1, alt)?.date ?? null;
  const sunset = A.SearchRiseSet(A.Body.Sun, observer, -1, noon, 1)?.date ?? null;
  const base = sunset ? new Date(sunset.getTime() + 60e3) : noon;
  const civilDusk = find(-1, -6, noon);
  const nauticalDusk = find(-1, -12, noon);
  const astroDusk = find(-1, -18, noon);
  const astroDawn = find(+1, -18, base);
  const nauticalDawn = find(+1, -12, base);
  const civilDawn = find(+1, -6, base);
  const sunrise = A.SearchRiseSet(A.Body.Sun, observer, +1, base, 1)?.date ?? null;
  return { sunset, civilDusk, nauticalDusk, astroDusk, astroDawn, nauticalDawn, civilDawn, sunrise };
}

/**
 * The night to show at time `t`: the one in progress until its sunrise, then the coming one. It starts from local
 * solar noon, so time zones and clock changes do not matter. Where the Sun does not rise (polar night or day),
 * it is the night after the most recent noon.
 */
export function observingNight(observer: A.Observer, t: Date): TwilightTimes & { noon: Date } {
  // The most recent noon: before midday this is yesterday's, so the night in progress is shown.
  let noon = A.SearchHourAngle(A.Body.Sun, observer, 0, new Date(t.getTime() - 86400e3)).time.date;
  let tw = twilight(observer, noon);
  // Once that night has ended (late morning), look ahead to the coming night instead.
  if (tw.sunrise && tw.sunrise.getTime() < t.getTime()) {
    noon = A.SearchHourAngle(A.Body.Sun, observer, 0, new Date(noon.getTime() + 3600e3)).time.date;
    tw = twilight(observer, noon);
  }
  return { noon, ...tw };
}

/**
 * Noctilucent clouds: seen in high summer from about 45° to 65° latitude, low towards the pole, while
 * the Sun is 6° to 16° below the horizon (British Astronomical Association guidance). Returns the
 * evening and morning windows for the night after `noon`, or null out of season or out of range.
 */
export function noctilucentWindows(observer: A.Observer, noon: Date): { evening: [Date, Date] | null; morning: [Date, Date] | null } | null {
  const lat = observer.latitude;
  if (Math.abs(lat) < 45 || Math.abs(lat) > 66) return null;
  const start = Date.UTC(noon.getUTCFullYear(), 0, 1);
  const day = Math.floor((noon.getTime() - start) / 86400e3) + 1; // day of year
  const inSeason = lat > 0 ? day >= 135 && day <= 222 : day >= 319 || day <= 41; // mid-May to early Aug; mid-Nov to early Feb
  if (!inSeason) return null;
  const find = (dir: number, alt: number, from: Date) => A.SearchAltitude(A.Body.Sun, observer, dir, from, 1, alt)?.date ?? null;
  const dusk6 = find(-1, -6, noon);
  if (!dusk6) return null;
  const dusk16 = find(-1, -16, dusk6);
  const dawn6 = find(+1, -6, dusk6);
  const dawn16 = find(+1, -16, dusk6);
  // Where the Sun never sinks to 16° below, the window runs all night.
  if (!dusk16 || !dawn16 || (dawn6 && dusk16 > dawn6)) return { evening: dawn6 ? [dusk6, dawn6] : null, morning: null };
  return { evening: [dusk6, dusk16], morning: dawn6 ? [dawn16, dawn6] : null };
}

/**
 * Planetocentric latitude and east longitude (degrees) of the point on a planet facing the observer, from
 * the IAU rotation model (pole and prime-meridian angle), taken at the time the light left the planet.
 */
export function subObserverPoint(body: A.Body, date: Date, dir: Vec3, distAu: number): { lat: number; lon: number } {
  const lightTime = distAu * 499.004784 * 1000; // ms
  const axis = A.RotationAxis(body, new Date(date.getTime() - lightTime));
  const P: Vec3 = norm([axis.north.x, axis.north.y, axis.north.z]);
  const node = norm([-P[1], P[0], 0]); // ICRF north × pole
  const w = (axis.spin * Math.PI) / 180;
  const pxn: Vec3 = [P[1] * node[2] - P[2] * node[1], P[2] * node[0] - P[0] * node[2], P[0] * node[1] - P[1] * node[0]];
  const M: Vec3 = [node[0] * Math.cos(w) + pxn[0] * Math.sin(w), node[1] * Math.cos(w) + pxn[1] * Math.sin(w), node[2] * Math.cos(w) + pxn[2] * Math.sin(w)];
  const Y: Vec3 = [P[1] * M[2] - P[2] * M[1], P[2] * M[0] - P[0] * M[2], P[0] * M[1] - P[1] * M[0]];
  const D: Vec3 = [-dir[0], -dir[1], -dir[2]]; // from the planet towards us
  const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  return { lat: (Math.asin(Math.max(-1, Math.min(1, dot(D, P)))) * 180) / Math.PI, lon: (Math.atan2(dot(D, Y), dot(D, M)) * 180) / Math.PI };
}
